/**
 * App Component
 * ---------------------------------------------------
 * Root-level component that sets up:
 *  - Authentication (Okta or Azure MSAL)
 *  - Global Context (SyncUpProductContext)
 *  - Application Routes
 *  - Lazy/Suspense handling for dynamic components
 *
 * Supports:
 *  - Multiple app variants (KidsWatch, DriveLite, etc.)
 *  - Hosted pages (FAQ, Chatbot FAQ, Lost Mode)
 *  - Graceful fallbacks for unsupported routes
 */

import React, { Suspense } from 'react';
import { Route, Switch } from 'react-router-dom';
import { MsalProvider } from '@azure/msal-react';

// Authentication and main app imports
import Callback from './auth/Callback';
import CallbackMsal from 'auth/CallbackMsal';
import Login from './pages/Login';
import Main from './pages/Main';

// Core UI components and utilities
import Svgs from './components/uiHelpers/Svgs';
import Status from './components/status/Status';
import SyncUpProductContext from './context/SyncUpProductContext';
import DriveLite from './components/poc/drivelite/DriveLite';
import usePersistableState from './hooks/usePersistableState';

// Hosted pages and product-specific components
import KidswatchPlayStore from './pages/KidswatchPlayStore';
import LostModeTlife from './pages/hostedPages/LostModeTlife';
import Error from './pages/Error';
import FaqPage from './pages/hostedApps/faq/FAQPage';
import KidsChatBotFAQ from './pages/hostedApps/chatBot/KidsChatBotFAQ';

// Constants and configuration values
import {
  APP_MOBILE,
  STORAGE_TYPES,
  IS_AZURE_AUTH
} from './utils/app_constants';

// Design system route placeholder (currently disabled)
// import { DESIGN_SYSTEM_ROUTES } from './hostedApps/mds/utils/app_constants';
// const DesignSystem = React.lazy(() => import('./hostedApps/mds/pages/Main'));

function App({ oktaBaseUrl, useOktaWidget, instance }) {
  /**
   * Persist selected SyncUp app across reloads using localStorage.
   * This custom hook stores data in the specified storage medium (LOCAL or SESSION).
   */
  const [syncUpApp, setSyncUpApp] = usePersistableState({
    storageKey: `SyncUp-Product`,
    storageType: STORAGE_TYPES.LOCAL
  });

  /**
   * SyncUp context value
   * Shared across the app for identifying which SyncUp product is active.
   */
  const syncUpProduct = {
    syncUpAppName: syncUpApp,
    setSyncUpApp
  };

  /**
   * getFAQPath()
   * -----------------------------------------------
   * Dynamically generates route paths for FAQ pages
   * based on APP_MOBILE configuration.
   *
   * For example:
   *  - /kidswatch/faq → loads FaqPage
   *  - Other app types → loads Error component
   */
  const getFAQPath = () => {
    let paths = [];
    Object.entries(APP_MOBILE).forEach(([key, value]) => {
      key === 'WATCH' && value
        ? paths.push({
            path: `/${value}/faq`,
            key: `${key}`,
            component: FaqPage
          })
        : paths.push({
            path: `/${value}/faq`,
            key: `${key}`,
            component: Error
          });
    });
    return paths;
  };

  /**
   * getChatBotFAQPath()
   * -----------------------------------------------
   * Similar to getFAQPath, but for the ChatBot FAQ endpoint.
   * Used to generate chatbot ingestion routes dynamically.
   *
   * For example:
   *  - /kidswatch/faq/chatbot → loads KidsChatBotFAQ
   *  - Other app types → loads Error component
   */
  const getChatBotFAQPath = () => {
    let paths = [];
    Object.entries(APP_MOBILE).forEach(([key, value]) => {
      key === 'WATCH' && value
        ? paths.push({
            path: `/${value}/faq/chatbot`,
            key: `${key}_CHATBOT`,
            component: KidsChatBotFAQ
          })
        : paths.push({
            path: `/${value}/faq/chatbot`,
            key: `${key}_CHATBOT`,
            component: Error
          });
    });
    return paths;
  };

  /**
   * Route Definitions
   * -----------------------------------------------
   * Using <Switch> to ensure only one route matches.
   * Includes all top-level pages and fallback routing.
   */
  const routes = (
    <Switch>
      {/* Login and Callback routes */}
      <Route
        path="/login"
        render={() => (
          <Login baseUrl={oktaBaseUrl} isUsingOktaWidget={useOktaWidget} />
        )}
      />
      <Route
        path="/callback"
        component={IS_AZURE_AUTH ? CallbackMsal : Callback}
      />

      {/* Product and test routes */}
      <Route path={'/drive'} component={DriveLite} />
      <Route path={'/testharness/drivelite'} component={DriveLite} />
      <Route path={'/tlife-lostmode'} component={LostModeTlife} />
      <Route path={'/kidswatch-app'} component={KidswatchPlayStore} />

      {/* Dynamic FAQ and ChatBot FAQ routes */}
      {getFAQPath().map(e => (
        <Route exact key={e.key} path={e.path} component={e.component} />
      ))}
      {getChatBotFAQPath().map(e => (
        <Route exact key={e.key} path={e.path} component={e.component} />
      ))}

      {/* Future design system route (currently commented out) */}
      {/* <Route exact path={DESIGN_SYSTEM_ROUTES} component={DesignSystem} /> */}

      {/* Fallback route: if no route matches, load Main */}
      <Route component={Main} />
    </Switch>
  );

  /**
   * App Component Render
   * -----------------------------------------------
   * Wraps routes in context and shared UI providers:
   *  - SyncUpProductContext → global state management
   *  - Status → handles system and connectivity status UI
   *  - Svgs → global SVG sprite loader
   *  - Suspense → fallback for lazy-loaded components
   *  - MsalProvider → Azure AD authentication provider (if enabled)
   */
  return (
    <SyncUpProductContext.Provider value={syncUpProduct}>
      <Status>
        {/* Global SVG symbols and icons */}
        <Svgs />

        {/* Suspense fallback for lazy-loaded routes */}
        <Suspense fallback={<>...Loading</>}>
          {/* Conditionally wrap routes with Azure authentication context */}
          {IS_AZURE_AUTH ? (
            <MsalProvider instance={instance}>{routes}</MsalProvider>
          ) : (
            routes
          )}
        </Suspense>
      </Status>
    </SyncUpProductContext.Provider>
  );
}

export default App;
